/// <amd-module name="Build/Settings/SettingsView" />
import * as React from "react";

import * as Resources from "Build/Settings/Resources";
import { SettingsToggle } from "Build/SettingsToggle/SettingsToggle";
import { GeneralSettingsData } from "Build/Client/Contracts/DataProvider";

import { GeneralSettingsView } from "general/GeneralSettingsView";
import { GithubForkProtectionSettingsView } from "github-fork-protection/GithubForkProtectionSettingsView";
import { RetentionSettingsView } from "retention/RetentionSettingsView";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentProperties, VssComponent, IVssComponentContext} from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";

import { IGeneralSettingsService } from "general/GeneralSettingsService";

class PipelineSettingsView extends VssComponent<IVssComponentProperties> {
    private _service: IGeneralSettingsService;
    private _settings: IReadonlyObservableValue<GeneralSettingsData>;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);
        this._service = this.context.pageContext.getService<IGeneralSettingsService>("IGeneralSettingsService");
        this._settings = this._service.data;
    }

    public render(): JSX.Element {
        const isGithubForkProtectionEnabled = this._showGithubForkProtectionSettings();
        const isDisableImpliedYAMLCiTriggerEnabled = this._showDisableImpliedYAMLCiTriggerSettings();
        
        return (
            <div className="bolt-page-grey pipeline-settings flex flex-column flex-grow">
                <Header title={Resources.Settings} titleSize={TitleSize.Large} />

                {this._showProjectRetentionSettings() &&
                    <Card className="bolt-card-white pipelines-settings-card" titleProps={{ text: Resources.RetentionPolicy, size: TitleSize.Small }}>
                        <div>
                            <RetentionSettingsView />
                        </div>
                    </Card>
                }

                <Card className="bolt-card-white pipelines-settings-card" titleProps={{ text: Resources.General, size: TitleSize.Small }} contentProps={{ contentPadding: false }}>
                    <div>
                        <GeneralSettingsView />
                    </div>
                </Card>

                {(isGithubForkProtectionEnabled || isDisableImpliedYAMLCiTriggerEnabled) &&
                    <Card className="bolt-card-white pipelines-settings-card" titleProps={{ text: Resources.Triggers, size: TitleSize.Small }} contentProps={{ contentPadding: false }}>
                        <div className="settings-view">
                            { isGithubForkProtectionEnabled &&
                                <div className="flex-row">
                                    <GithubForkProtectionSettingsView />
                                </div>
                            }
                            { isDisableImpliedYAMLCiTriggerEnabled && this._getDisableImpliedYAMLCiTriggerContent() }
                        </div>
                    </Card>
                }
            </div>
        );
    }

    private _showProjectRetentionSettings(): boolean {
        return isFeatureFlagEnabled(this.context.pageContext, "Pipelines.SimplifiedRetention", false) &&
            isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.PipelineBillingModel2", false);
    }

    private _showGithubForkProtectionSettings(): boolean {
        return isFeatureFlagEnabled(this.context.pageContext, "Build2.AllowCentralizedPipelineControls", false);
    }

    private _showDisableImpliedYAMLCiTriggerSettings(): boolean {
        return isFeatureFlagEnabled(this.context.pageContext, "Build2.DisableImpliedYAMLCiTrigger", false);
    }

    private _getDisableImpliedYAMLCiTriggerContent(): JSX.Element {
        return (
            <div className="flex-row">
                <Observer settings={this._settings}>
                    {
                        (observerProps: { settings: GeneralSettingsData }) => {
                            if (observerProps.settings.disableImpliedYAMLCiTrigger) {
                                return <SettingsToggle
                                    checked={observerProps.settings.disableImpliedYAMLCiTrigger.enabled || observerProps.settings.disableImpliedYAMLCiTrigger.orgEnabled}
                                    title={Resources.DisableImpliedYAMLCiTriggerTitle}
                                    description={Resources.DisableImpliedYAMLCiTriggerDescription}
                                    onChange={(e, checked) => this._service.updateDisableImpliedYAMLCiTrigger(checked)}
                                    disabled={observerProps.settings.viewOnlyPermissions || (observerProps.settings.disableImpliedYAMLCiTrigger && observerProps.settings.disableImpliedYAMLCiTrigger.orgEnabled) }
                                    toggleTooltipProps={observerProps.settings.disableImpliedYAMLCiTrigger.orgEnabled ? { text: Resources.SettingEnforcedOrgLevel } : undefined}
                                />
                            } else {
                                return null;
                            }
                        }
                    }
                </Observer>
            </div>
        )    
    }
}

Components.add("pipelines-settings-view", PipelineSettingsView);
